import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Tabs, Tab, Grid, Paper, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import { SwapHoriz as SwitchIcon, MoveDown as MoveIcon } from "@mui/icons-material";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import TransferDialog from "./TransferDialog";
import SwitchDialog from "./SwitchDialog";

const GRADES = [
  "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"
];

const ACADEMIC_YEAR = "2026-2027";

const ClassroomManagement = () => {
  const [selectedGrade, setSelectedGrade] = useState(0);
  const [sections, setSections] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const grade = GRADES[selectedGrade];

  const loadSections = useCallback(async () => {
    try {
      const result = await fetchData(`admin/admission/sections?grade=${encodeURIComponent(grade)}&academicYear=${ACADEMIC_YEAR}`);
      setSections(result?.data || []);
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load sections" });
    }
  }, [grade]);

  useEffect(() => { loadSections(); }, [loadSections]);

  const handleSuccess = (msg) => {
    setToast({ open: true, severity: "success", message: msg });
    setTransferOpen(false);
    setSwitchOpen(false);
    loadSections();
  };

  const totalStudents = sections.reduce((sum, s) => sum + (s.enrolledCount || 0), 0);
  const totalClass = new Set(sections.map(s => s.grade)).size;
  const classFilled = sections.filter(s => (s.enrolledCount || 0) >= (s.maxCapacity || 24)).length;

  const summaryCards = [
    { label: "Total Sections", value: sections.length, color: "#244C77" },
    { label: "Enrolled",       value: totalStudents,   color: "#2E7D32" },
    { label: "Total Class",    value: totalClass,      color: "#ED8B00" },
    { label: "Class Filled",   value: classFilled,     color: "#1565c0" },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Classroom Management</Typography>
      <Typography variant="h5" fontWeight="bold" mb={3}>Classroom Management</Typography>

      {/* Summary */}
      <Grid container spacing={2} mb={3}>
        {summaryCards.map((c) => (
          <Grid item xs={6} sm={3} key={c.label}>
            <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2 }}>
              <Typography variant="h4" fontWeight="bold" color={c.color}>{c.value}</Typography>
              <Typography variant="body2" color="text.secondary">{c.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Grade Tabs */}
      <Tabs value={selectedGrade} onChange={(_, v) => setSelectedGrade(v)}
        variant="scrollable" scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        {GRADES.map(g => <Tab key={g} label={g} />)}
      </Tabs>

      {/* Section Cards */}
      {sections.map(sec => (
        <Paper key={sec.id} sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6" fontWeight="bold">{sec.displayName}</Typography>
              <Chip label={`${sec.enrolledCount}/${sec.maxCapacity}`} size="small"
                color={sec.enrolledCount >= sec.maxCapacity ? "error" : "success"} />
            </Box>
          </Box>

          {sec.enrollments?.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Reg #</TableCell>
                    <TableCell>Student Name</TableCell>
                    <TableCell>DOB</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sec.enrollments.map(en => (
                    <TableRow key={en.id} hover>
                      <TableCell>{en.application?.registrationNumber}</TableCell>
                      <TableCell>{en.application?.firstName} {en.application?.lastName}</TableCell>
                      <TableCell>{en.application?.dob}</TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<MoveIcon />} sx={{ textTransform: "none", mr: 1 }}
                          onClick={() => { setSelectedStudent({ ...en.application, currentSectionId: sec.id, currentSection: sec.displayName }); setTransferOpen(true); }}>
                          Move
                        </Button>
                        <Button size="small" startIcon={<SwitchIcon />} sx={{ textTransform: "none" }}
                          onClick={() => { setSelectedStudent({ ...en.application, currentSectionId: sec.id, currentSection: sec.displayName }); setSwitchOpen(true); }}>
                          Switch
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">No students in this section</Typography>
          )}
        </Paper>
      ))}

      {selectedStudent && (
        <>
          <TransferDialog open={transferOpen} onClose={() => setTransferOpen(false)}
            student={selectedStudent} sections={sections} onSuccess={handleSuccess} />
          <SwitchDialog open={switchOpen} onClose={() => setSwitchOpen(false)}
            student={selectedStudent} sections={sections} onSuccess={handleSuccess} />
        </>
      )}

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default ClassroomManagement;
